/**
 * Reference List Tab Manager
 *
 * Provides instant tab switching with in-memory caching, shared DocumentFragment rendering,
 * deduplicated background fetching for deferred tabs, accessible screen-reader status,
 * and standard WAI-ARIA keyboard navigation.
 */
export default class ReferenceTabs {
    constructor() {
        this.tablist = document.querySelector('.tabs[role="tablist"]');
        this.tabs = Array.from(document.querySelectorAll('.tabs [role="tab"]'));
        this.container = document.querySelector('.reference-list');

        if (!this.tabs.length || !this.container) {
            return;
        }

        this.activeTab = 'name';
        this.domCache = new Map();
        this.pendingRequests = new Map();

        this.initStatusAnnouncer();
        this.initInitialView();
        this.bindEvents();
    }

    /**
     * Initializes screen-reader live region for accessible announcements.
     */
    initStatusAnnouncer() {
        this.statusAnnouncer = document.getElementById('reference-status');
        if (!this.statusAnnouncer) {
            this.statusAnnouncer = document.createElement('div');
            this.statusAnnouncer.id = 'reference-status';
            this.statusAnnouncer.className = 'sr-only';
            this.statusAnnouncer.setAttribute('role', 'status');
            this.statusAnnouncer.setAttribute('aria-live', 'polite');
            this.statusAnnouncer.setAttribute('aria-atomic', 'true');
            this.container.parentNode?.insertBefore(this.statusAnnouncer, this.container);
        }
    }

    /**
     * Announces a message to assistive technology.
     */
    announce(message) {
        if (this.statusAnnouncer) {
            this.statusAnnouncer.textContent = message;
        }
    }

    /**
     * Caches the server-rendered initial "name" view DOM nodes.
     */
    initInitialView() {
        const initialTab = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        if (initialTab) {
            this.activeTab = initialTab.dataset.tab || 'name';
        }

        const fragment = document.createDocumentFragment();
        const nonLoaderChildren = Array.from(this.container.children).filter(
            child => !child.classList.contains('loader')
        );

        nonLoaderChildren.forEach(child => {
            fragment.appendChild(child.cloneNode(true));
        });

        this.domCache.set(this.activeTab, fragment);
    }

    /**
     * Binds click and keyboard navigation events.
     */
    bindEvents() {
        this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();
                this.switchTab(tab);
            });

            tab.addEventListener('keydown', (e) => {
                this.handleKeyDown(e, index);
            });
        });
    }

    /**
     * WAI-ARIA tab keyboard navigation handler.
     */
    handleKeyDown(e, index) {
        let newIndex = null;

        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault();
                newIndex = (index + 1) % this.tabs.length;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault();
                newIndex = (index - 1 + this.tabs.length) % this.tabs.length;
                break;
            case 'Home':
                e.preventDefault();
                newIndex = 0;
                break;
            case 'End':
                e.preventDefault();
                newIndex = this.tabs.length - 1;
                break;
            default:
                return;
        }

        if (newIndex !== null) {
            const targetTab = this.tabs[newIndex];
            targetTab.focus();
            this.switchTab(targetTab);
        }
    }

    /**
     * Switches to the requested tab.
     */
    async switchTab(targetTab) {
        const tabType = targetTab.dataset.tab;
        if (!tabType) return;

        if (this.activeTab === tabType && targetTab.getAttribute('aria-selected') === 'true') {
            return;
        }

        // Update tab selected states
        this.tabs.forEach(tab => {
            const isSelected = tab === targetTab;
            tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            tab.setAttribute('tabindex', isSelected ? '0' : '-1');
        });

        this.container.setAttribute('aria-labelledby', targetTab.id || `tab-${tabType}`);
        this.activeTab = tabType;

        // 1. In-memory cache hit: instant switch
        if (this.domCache.has(tabType)) {
            this.container.classList.remove('is-loading');
            this.setContainerContent(this.domCache.get(tabType));
            this.announce(`Prikazane reference: ${targetTab.textContent.trim()}`);
            return;
        }

        // 2. Fetch data from local endpoint
        this.container.classList.add('is-loading');

        try {
            let fetchPromise = this.pendingRequests.get(tabType);
            if (!fetchPromise) {
                fetchPromise = this.fetchTabData(tabType);
                this.pendingRequests.set(tabType, fetchPromise);
            }

            const groups = await fetchPromise;
            this.pendingRequests.delete(tabType);

            // Generate DOM fragment using shared renderer and cache it
            const fragment = this.renderTabGroups(groups);
            this.domCache.set(tabType, fragment);

            // If user is still viewing this tab, apply the content
            if (this.activeTab === tabType) {
                this.setContainerContent(fragment);
                this.container.classList.remove('is-loading');
                this.announce(`Prikazane reference: ${targetTab.textContent.trim()}`);
            }
        } catch (error) {
            this.pendingRequests.delete(tabType);
            if (this.activeTab === tabType) {
                this.container.classList.remove('is-loading');
                this.renderErrorState(targetTab, tabType);
                this.announce('Greška pri dohvaćanju referenci. Pokušajte ponovno.');
            }
        }
    }

    /**
     * Fetches reference data from the local WordPress endpoint.
     */
    async fetchTabData(tabType) {
        const ajaxUrl = (typeof w4d_frontend !== 'undefined' && w4d_frontend.url)
            ? w4d_frontend.url
            : '/wp-admin/admin-ajax.php';
        const nonce = (typeof w4d_frontend !== 'undefined' && w4d_frontend.nonce)
            ? w4d_frontend.nonce
            : '';

        const params = new URLSearchParams({
            action: 'w4d_get_references',
            tab: tabType,
            _nonce: nonce,
        });

        const response = await fetch(`${ajaxUrl}?${params.toString()}`, {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            throw new Error(`Server returned HTTP ${response.status}`);
        }

        const json = await response.json();
        if (!json.success || !json.data || !Array.isArray(json.data.groups)) {
            throw new Error(json.data?.message || 'Invalid server response');
        }

        return json.data.groups;
    }

    /**
     * Shared group/item DOM renderer using DocumentFragment and safe DOM methods.
     */
    renderTabGroups(groups) {
        const fragment = document.createDocumentFragment();

        for (let i = 0; i < groups.length; i++) {
            const group = groups[i];
            const ol = document.createElement('ol');
            const items = Array.isArray(group.items) ? group.items : [];

            for (let j = 0; j < items.length; j++) {
                const item = items[j];
                const li = document.createElement('li');
                const p = document.createElement('p');
                const strong = document.createElement('strong');
                strong.textContent = item.name || '';
                p.appendChild(strong);
                p.appendChild(document.createTextNode(`, ${item.city || ''}`));
                li.appendChild(p);

                if (Array.isArray(item.modules) && item.modules.length > 0) {
                    const ul = document.createElement('ul');
                    ul.setAttribute('aria-label', 'Korišteni moduli');
                    for (let m = 0; m < item.modules.length; m++) {
                        const modLi = document.createElement('li');
                        modLi.textContent = item.modules[m];
                        ul.appendChild(modLi);
                    }
                    li.appendChild(ul);
                }

                ol.appendChild(li);
            }

            if (group.title) {
                const section = document.createElement('section');
                const h2 = document.createElement('h2');
                h2.textContent = group.title;
                section.appendChild(h2);
                section.appendChild(ol);
                fragment.appendChild(section);
            } else {
                fragment.appendChild(ol);
            }
        }

        return fragment;
    }

    /**
     * Replaces container children while keeping the loader element intact.
     */
    setContainerContent(contentFragment) {
        const loader = this.container.querySelector('.loader');
        const children = Array.from(this.container.children);

        for (let i = 0; i < children.length; i++) {
            if (children[i] !== loader) {
                children[i].remove();
            }
        }

        if (contentFragment) {
            this.container.appendChild(contentFragment.cloneNode(true));
        }
    }

    /**
     * Renders a user-friendly error state with retry button.
     */
    renderErrorState(targetTab, tabType) {
        const loader = this.container.querySelector('.loader');
        const children = Array.from(this.container.children);

        for (let i = 0; i < children.length; i++) {
            if (children[i] !== loader) {
                children[i].remove();
            }
        }

        const errorContainer = document.createElement('div');
        errorContainer.className = 'reference-error';
        errorContainer.setAttribute('role', 'alert');

        const message = document.createElement('p');
        message.textContent = 'Došlo je do pogreške pri učitavanju podataka.';
        errorContainer.appendChild(message);

        const retryBtn = document.createElement('button');
        retryBtn.type = 'button';
        retryBtn.textContent = 'Pokušaj ponovno';
        retryBtn.addEventListener('click', () => {
            this.switchTab(targetTab);
        });
        errorContainer.appendChild(retryBtn);

        this.container.appendChild(errorContainer);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new ReferenceTabs();
});
